import ScrollPanel from '@kpv-lab/scrollable-panel'
import { DraggablePanel } from '@kpv-lab/ui'
import classNames from 'classnames'
import React, { PureComponent } from 'react'
import { connect, ConnectedProps } from 'react-redux'

import {
  hideLinkEditor as hideLinkEditorAction,
  searchLinks as searchLinksAction,
} from './state/actions'
import { OwnState as LinkEditorReduxState } from './state/reducer'

// map the css class name to the tag group id
export const tagGroupIds = {
  person: 1,
  place: 2,
  event: 3,
  creation: 4,
}

export interface OwnProps {
  cancelHandler?: () => void
  popupPanel: boolean
}

export type AllProps = OwnProps & TypesFromRedux

interface OwnState {
  queryText: string
  parentId: number
}
const mapStateToProps = (state: { linkEditor: LinkEditorReduxState }) => {
  const {
    query,
    itemId,
    templateId,
    links,
    link,
    pending,
    updateHandler,
    clearHandler,
  } = state.linkEditor

  return {
    query,
    itemId,
    templateId,
    links,
    link,
    pending,
    updateHandler,
    clearHandler,
  }
}

const connector = connect(mapStateToProps, {
  hideLinkEditor: hideLinkEditorAction,
  searchLinks: searchLinksAction,
})

type TypesFromRedux = ConnectedProps<typeof connector>

export class LinkEditor extends PureComponent<AllProps, OwnState> {
  static defaultProps = {
    popupPanel: true,
  }

  timeout = 0
  refSearch: React.RefObject<any>
  state = {
    queryText: '',
    parentId: 0,
  }

  constructor(props) {
    super(props)
    const { query } = this.props

    this.state = {
      queryText: query,
      parentId: 0,
    }

    this.refSearch = React.createRef()
  }

  componentDidMount() {
    const { query, itemId, searchLinks, templateId } = this.props
    searchLinks(itemId, query, templateId)

    this.refSearch.current?.focus()
  }

  componentWillUnmount(): void {
    window.clearTimeout(this.timeout)
  }

  handleSearch = (query) => {
    const { itemId, templateId, searchLinks } = this.props

    window.clearTimeout(this.timeout)
    this.timeout = window.setTimeout(() => {
      searchLinks(itemId, query, templateId)
    }, 700)
  }

  changeHandler = (e) => {
    const query = e.target.value
    this.setState({ queryText: query })
    this.handleSearch(query)
  }

  searchButtonClicked = () => {
    const { queryText } = this.state
    this.handleSearch(queryText)
  }

  acceptHandler = (event) => {
    const { hideLinkEditor, updateHandler } = this.props
    const el = event.target.closest('.link-editor-row, .link-editor-current')
    const {
      id,
      title,
      start,
      end,
      type,
      parentid: parentId,
      templateid: templateId,
      partner,
    } = el.dataset

    updateHandler &&
      updateHandler({
        id: parseInt(id),
        title,
        start,
        end,
        type,
        parentId,
        templateId,
        partner,
      })
    hideLinkEditor()
  }

  clearHandler = (event) => {
    const { hideLinkEditor, itemId } = this.props

    event.preventDefault()
    event.stopPropagation()

    this.props.clearHandler?.(itemId)
    hideLinkEditor()
  }

  cancelHandler = () => {
    const { cancelHandler, hideLinkEditor } = this.props
    if (cancelHandler) {
      cancelHandler()
    } else {
      hideLinkEditor()
    }
  }

  tagTypeHandler = (event) => {
    this.setState({ parentId: parseInt(event.target.value) })
  }

  render() {
    const { link, links, pending, popupPanel, itemId } = this.props
    const { queryText } = this.state

    const clearButton = (
      <button
        className="btn btn-icon link-editor-clear-btn"
        onClick={this.clearHandler}
        title="Remove this link"
      >
        <i className="material-icons md-18">remove_circle_outline</i>
      </button>
    )

    let linkRow
    if (link && (link['id'] || link['_id'])) {
      // we have a valid link or tag

      const info = <span className="link-editor-info">{link['_type']}</span>
      const title = link['name'] || link['title']
      linkRow = (
        <section
          className="link-editor-current"
          data-id={link['id'] || link['_id']}
          data-parentid={link['_parentId']}
          data-templateid={link['_globalTplId']}
          data-title={title}
          data-start={link['start'] || link['_start']}
          data-end={link['end'] || link['_end']}
          data-type={link['type'] || link['_type']}
          data-partner={link['partner'] || link['_partner']}
          onClick={this.acceptHandler}
        >
          <strong className={'link-editor-title link-title-itemlink'}>
            {title}
          </strong>
          {info}
          {clearButton}
        </section>
      )
    }

    const rows = links.map((row, idx) => {
      const id = row['_id']

      return (
        <div
          className={classNames('link-editor-row', { active: id === itemId })}
          key={idx}
          data-id={id}
          data-parentid={row['_parentId']}
          data-templateid={row['_globalTplId']}
          data-title={row['title']}
          data-start={row['_start']}
          data-end={row['_end']}
          data-type={row['_type']}
          data-partner={row['_partner']}
          data-cy={`link-row-${row['title']}`}
          onClick={this.acceptHandler}
        >
          <strong className="link-editor-title">{row['title']}</strong>
          <span className="link-editor-info">{row['_type']}</span>
        </div>
      )
    })

    const linkEditorBody = (
      <div className="link-editor-body">
        <h2 className={classNames('ui-title', { draggable: popupPanel })}>
          Link Selector
        </h2>
        <i className="material-icons ui-close-btn" onClick={this.cancelHandler}>
          close
        </i>

        <header
          className={classNames('link-editor-header', { loading: pending })}
        >
          <input
            ref={this.refSearch}
            type="text"
            onChange={this.changeHandler}
            data-cy="link-editor-input"
            className="link-editor-query"
            value={queryText}
            placeholder="Keyword search..."
          />
          <button className="btn btn-icon" onClick={this.searchButtonClicked}>
            <i className="material-icons md-18">search</i>
          </button>
          {linkRow}
        </header>

        <ScrollPanel
          id="link-editor-results"
          panelClassName="link-editor-results"
        >
          {rows}
        </ScrollPanel>
      </div>
    )

    let panel
    if (popupPanel) {
      panel = (
        <DraggablePanel
          className="ui-floating-panel link-editor"
          name="linkEditor"
          backdrop={true}
          backdropHandler={this.cancelHandler}
        >
          {linkEditorBody}
        </DraggablePanel>
      )
    }

    return (popupPanel && panel) || linkEditorBody
  }
}

export default connector(LinkEditor)
